// Ein Dialog, aus dem die Tabulatortaste herausfuehrt, ist fuer jemanden ohne Maus schlimmer als
// kein Dialog: er bedient dann unsichtbar die Seite dahinter — und die Seite dahinter enthaelt in
// dieser Anwendung Knoepfe, die Geld bewegen. Diese Falle haelt den Fokus im Dialog, schliesst mit
// Escape und gibt den Fokus beim Schliessen genau dorthin zurueck, wo er herkam.
//
// Wohin der Fokus beim Oeffnen faellt, entscheidet der Dialog, nicht die Reihenfolge im DOM: ein
// `data-erstfokus` an einem bedienbaren Element zieht ihn dorthin, sonst faellt er auf das erste.
// Der Grund ist keine Feinheit. Ein <button> loest bei *jedem* keydown aus, auch bei den
// Wiederholungen, die der Browser nach etwa einer halben Sekunde gedrueckt gehaltener Eingabetaste
// schickt. Faellt der Fokus auf „Jetzt freigeben“, dann vergibt eine zu lange gehaltene oder
// zweimal getippte Eingabetaste auf „Freigeben“ eine Rechnungsnummer, ohne dass der
// Bestaetigungstext je gelesen wurde. Deshalb traegt in den beiden Ja/Nein-Dialogen dieser
// Anwendung „Abbrechen“ die Marke: die Wiederholung landet dort, wo sie nichts anrichtet.
import { useEffect, useRef, type RefObject } from 'react'

const FOKUSSIERBAR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export function fokussierbareElemente(wurzel: HTMLElement): HTMLElement[] {
  return Array.from(wurzel.querySelectorAll<HTMLElement>(FOKUSSIERBAR)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true' && el.closest('[aria-hidden="true"]') === null,
  )
}

export function useFokusfalle(
  kasten: RefObject<HTMLElement | null>,
  offen: boolean,
  onSchliessen: () => void,
): void {
  // Der Rueckruf liegt in einer Ref, damit ein inline uebergebenes () => setOffen(false) den Effekt
  // nicht bei jedem Rendern neu startet — das wuerde den Fokus staendig auf das erste Feld
  // zuruecksetzen, waehrend der Benutzer tippt.
  const schliessen = useRef(onSchliessen)
  useEffect(() => {
    schliessen.current = onSchliessen
  })

  useEffect(() => {
    if (!offen) return
    const gefunden = kasten.current
    if (gefunden === null) return
    // Der Typ steht hier ausdruecklich statt aus der Einengung darueber: `beiTaste` unten ist eine
    // hochgezogene Funktionsdeklaration, und in deren Rumpf traegt tsc die Einengung eines
    // geschlossenen `const` nicht mit — `wurzel` waere dort wieder `HTMLElement | null`.
    const wurzel: HTMLElement = gefunden

    const vorher = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const bedienbare = fokussierbareElemente(wurzel)
    const markiert = bedienbare.find((el) => el.hasAttribute('data-erstfokus'))
    const erstes = markiert ?? bedienbare[0]
    // Nur wenn die Falle den tabindex selbst gesetzt hat, raeumt sie ihn unten wieder weg: der
    // Kasten kann ein wiederverwendeter Knoten sein, und ein zurueckgelassenes tabindex="-1"
    // veraendert ihn ueber das Leben des Dialogs hinaus.
    let tabindexGesetzt = false
    if (erstes !== undefined) erstes.focus()
    else {
      tabindexGesetzt = !wurzel.hasAttribute('tabindex')
      wurzel.setAttribute('tabindex', '-1')
      wurzel.focus()
    }

    function beiTaste(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') {
        ev.preventDefault()
        schliessen.current()
        return
      }
      if (ev.key !== 'Tab') return
      const liste = fokussierbareElemente(wurzel)
      if (liste.length === 0) {
        ev.preventDefault()
        return
      }
      const anfang = liste[0]
      const ende = liste[liste.length - 1]
      if (anfang === undefined || ende === undefined) return
      const aktiv = document.activeElement
      const drinnen = aktiv instanceof Node && wurzel.contains(aktiv)
      if (ev.shiftKey && (aktiv === anfang || !drinnen)) {
        ev.preventDefault()
        ende.focus()
      } else if (!ev.shiftKey && (aktiv === ende || !drinnen)) {
        ev.preventDefault()
        anfang.focus()
      }
    }

    document.addEventListener('keydown', beiTaste, true)
    return () => {
      document.removeEventListener('keydown', beiTaste, true)
      if (tabindexGesetzt) wurzel.removeAttribute('tabindex')
      vorher?.focus()
    }
  }, [kasten, offen])
}
